import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addAnnouncementLink,
  createAnnouncement,
  deleteAnnouncement,
  deleteAnnouncementAttachment,
  getAnnouncement,
  listAnnouncements,
  removeAnnouncementLink,
  updateAnnouncement,
  uploadAnnouncementAttachment,
  uploadAnnouncementInlineImage,
  type CreateAnnouncementInput,
  type ListAnnouncementsFilters,
  type UpdateAnnouncementInput,
} from '@/lib/announcements'
import type { AnnouncementLinkEntityType } from '@riverpark/types'

export function useAnnouncements(filters: ListAnnouncementsFilters = {}) {
  return useQuery({
    queryKey: ['announcements', filters],
    queryFn: () => listAnnouncements(filters),
  })
}

export function useAnnouncement(id: string | undefined) {
  return useQuery({
    queryKey: ['announcements', id],
    queryFn: () => getAnnouncement(id!),
    enabled: !!id,
  })
}

export function useCreateAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAnnouncementInput) => createAnnouncement(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  })
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAnnouncementInput }) =>
      updateAnnouncement(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['announcements'] })
      qc.invalidateQueries({ queryKey: ['announcements', id] })
    },
  })
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  })
}

export function useUploadAnnouncementAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ announcementId, file }: { announcementId: string; file: File }) =>
      uploadAnnouncementAttachment(announcementId, file),
    onSuccess: (_data, { announcementId }) =>
      qc.invalidateQueries({ queryKey: ['announcements', announcementId] }),
  })
}

export function useUploadAnnouncementInlineImage() {
  return useMutation({
    mutationFn: ({ announcementId, file }: { announcementId: string; file: File }) =>
      uploadAnnouncementInlineImage(announcementId, file),
    // No cache invalidation — the image is inserted directly into the editor's local state via
    // its returned URL, not read back from the announcement query.
  })
}

export function useDeleteAnnouncementAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ announcementId, documentId }: { announcementId: string; documentId: string }) =>
      deleteAnnouncementAttachment(announcementId, documentId),
    onSuccess: (_data, { announcementId }) =>
      qc.invalidateQueries({ queryKey: ['announcements', announcementId] }),
  })
}

export function useAddAnnouncementLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      announcementId,
      entityType,
      entityId,
    }: {
      announcementId: string
      entityType: AnnouncementLinkEntityType
      entityId: string
    }) => addAnnouncementLink(announcementId, entityType, entityId),
    onSuccess: (_data, { announcementId }) =>
      qc.invalidateQueries({ queryKey: ['announcements', announcementId] }),
  })
}

export function useRemoveAnnouncementLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ announcementId, linkId }: { announcementId: string; linkId: string }) =>
      removeAnnouncementLink(announcementId, linkId),
    onSuccess: (_data, { announcementId }) =>
      qc.invalidateQueries({ queryKey: ['announcements', announcementId] }),
  })
}
